<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import VueApexCharts from 'vue3-apexcharts';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  color: {
    type: String,
    default: '#3b82f6',
  },
  gradientFrom: {
    type: String,
    default: '',
  },
  gradientTo: {
    type: String,
    default: '',
  },
  height: {
    type: [String, Number],
    default: 60,
  },
  width: {
    type: [String, Number],
    default: '100%',
  },
  strokeWidth: {
    type: Number,
    default: 2,
  },
  showTooltip: {
    type: Boolean,
    default: false,
  },
  sparkline: {
    type: Boolean,
    default: true,
  },
  categories: {
    type: Array,
    default: () => [],
  },
});

const chartRef = ref(null);
const isReady = ref(false);

const series = computed(() => [
  {
    name: 'Value',
    data: props.data,
  },
]);

const chartOptions = computed(() => ({
  chart: {
    type: 'area',
    sparkline: {
      enabled: props.sparkline,
    },
    toolbar: {
      show: false,
    },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 600,
    },
  },
  colors: [props.color],
  stroke: {
    curve: 'smooth',
    width: props.strokeWidth,
    lineCap: 'round',
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.4,
      opacityTo: 0.05,
      stops: [0, 100],
      colorStops: [
        {
          offset: 0,
          color: props.gradientFrom || props.color,
          opacity: 0.4,
        },
        {
          offset: 100,
          color: props.gradientTo || props.color,
          opacity: 0.05,
        },
      ],
    },
  },
  grid: {
    show: false,
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  xaxis: {
    categories: props.categories,
    labels: {
      show: false,
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    crosshairs: {
      show: false,
    },
  },
  yaxis: {
    show: false,
    min: arr => {
      const min = Math.min(...(arr || [0]));
      return min > 0 ? 0 : min * 1.1;
    },
  },
  tooltip: {
    enabled: props.showTooltip,
    theme: 'dark',
    x: {
      show: false,
    },
    y: {
      formatter: value => Math.round(value),
    },
    marker: {
      show: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  markers: {
    size: 0,
  },
}));

onMounted(() => {
  setTimeout(() => {
    isReady.value = true;
  }, 50);
});

watch(
  () => props.data,
  () => {
    if (chartRef.value) {
      chartRef.value.updateSeries(series.value);
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="apex-area-chart">
    <VueApexCharts
      v-if="isReady && data.length"
      ref="chartRef"
      type="area"
      :height="height"
      :width="width"
      :options="chartOptions"
      :series="series"
    />
    <div
      v-else-if="!data.length"
      class="flex items-center justify-center text-n-slate-10 text-xs"
      :style="{ height: `${height}px`, width }"
    >
      <span class="opacity-50">--</span>
    </div>
  </div>
</template>
